import { useCallback, useEffect, useRef } from 'react';

import Api from 'core/api';
import useFetch from 'hooks/useFetch';
import useLocalStorage from 'hooks/useLocalStorage';
import useMediaInfo from 'hooks/useMediaInfo';
import useUserRedux from 'hooks/useUserRedux';

import { isNonEmptyArray } from 'utils/arrays';
import getFullName from 'utils/getFullName';
import { ensureObject } from 'utils/objects';

export const INTERCOM_HASH_KEY = 'intercomUserHash';
const INTERCOM_ERROR_MESSAGE = 'Unexpected Application Error!';

const Intercom = () => {
  const observerRef = useRef(null);

  const { data: user, isLoggedIn } = useUserRedux();
  const { isMobile } = useMediaInfo();

  const [intercomUserHash, setIntercomUserHash] = useLocalStorage(
    INTERCOM_HASH_KEY,
    // we set this from localStorage for the users who has hash already in their local storage
    localStorage.getItem(INTERCOM_HASH_KEY),
  );

  // function that loads intercom.js script into "document.body" with custom options
  const loadIntercom = useCallback(() => {
    const w = window;
    const ic = w.Intercom;

    const {
      affiliations,
      currentStudentGrade,
      districtName,
      email,
      firstName,
      schoolName,
      sisId,
      studentNumber,
      studentPreviewCounselor,
      type,
    } = ensureObject(user);

    /*
      Intercom.js has an issue of error overlay showing up on UI and it's a 3rd party issue.
      To beat the consequence, we came up with a solution that removes the error overlay from screen when it happens.

      Logic:
      - Iterate through added elements via a mutation observer
      - If the new element is a react element, is a node, has a parent and has error message as text content, remove the parent.
    */
    const onShowIntercom = () => {
      const config = { childList: true, subtree: true };

      const callback = (mutationsList = []) => {
        mutationsList.forEach(mutation => {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach(node => {
              const hasNode = Boolean(node);
              const doesNodeHasParent = Boolean(node?.parentNode);
              const isNodeReactEl = node?.nodeType === Node.ELEMENT_NODE;
              const doesNodeIncludeError = node?.textContent?.includes(
                INTERCOM_ERROR_MESSAGE,
              );

              if (
                hasNode &&
                doesNodeHasParent &&
                isNodeReactEl &&
                doesNodeIncludeError
              ) {
                node?.parentNode?.remove();
              }
            });
          }
        });
      };

      const intercomContainer = document.getElementById('intercom-container');

      if (intercomContainer && !observerRef?.current) {
        observerRef.current = new MutationObserver(callback);
        observerRef.current.observe(
          document.getElementById('intercom-container'),
          config,
        );
      }
    };

    if (typeof ic === 'function') {
      ic('reattach_activator');
      ic('update', w.intercomSettings);
    } else {
      const script = document.createElement('script');
      script.id = 'intercom-script';
      script.src = 'https://widget.intercom.io/widget/onb94xow';
      script.async = true;
      script.type = 'text/javascript';

      let name = null;
      if (studentPreviewCounselor?.firstName) {
        name = getFullName(studentPreviewCounselor);
      } else if (firstName) {
        name = getFullName(user);
      }

      script.onload = () => {
        if (w.Intercom) {
          w.Intercom('onShow', onShowIntercom);
        }

        w.intercomSettings = {
          app_id: 'onb94xow',
          name,
          email: studentPreviewCounselor?.email
            ? `DEMO_${studentPreviewCounselor?.email}`
            : email,
          user_hash: intercomUserHash,
          user_type: typeof type === 'string' ? type : type.join(', '),
          district_name: districtName,
          school_name: schoolName,
          student_grade_year: currentStudentGrade,
          student_sis_id: sisId ? +sisId : null,
          student_number: studentNumber ? +studentNumber : null,
          affiliations: isNonEmptyArray(affiliations)
            ? affiliations.map(aff => aff.organization?.name).join(', ')
            : null,
          custom_launcher_selector: '.intercom-launcher',
          hide_default_launcher: isMobile,
        };
      };
      document.body.appendChild(script);
    }
  }, [intercomUserHash, isMobile, user]);

  const reloadScript = useCallback(() => {
    const script = document.getElementById('intercom-script');
    if (script) {
      document.body.removeChild(script);
    }
    loadIntercom();
  }, [loadIntercom]);

  // get user intercom hash
  useFetch({
    apiFunc: Api.slUsers.getIntercomUserHash,
    apiFuncName: 'slUsers.getIntercomUserHash',
    options: {
      onSuccess: data => setIntercomUserHash(data?.[INTERCOM_HASH_KEY]),
      enabled: isLoggedIn && !intercomUserHash,
    },
  });

  // everytime isMobile changes, we need to reload the intercom script to be able to get correct panel
  useEffect(() => {
    reloadScript();
  }, [isMobile, reloadScript]);

  useEffect(
    () => () => {
      // Cleanup observer on unmount
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    },
    [],
  );

  return null;
};

export default Intercom;
